export const MAIL_EXTENDED_SEARCH_FIELD_NAMES = ['contact', 'subject', 'text', 'filename', 'fileType'] as const;

const MAIL_EXTENDED_SEARCH_MINIMUM_QUERY_LENGTH = 4;

export type MailExtendedSearchFieldName = (typeof MAIL_EXTENDED_SEARCH_FIELD_NAMES)[number];

export type MailExtendedSearchValues = Record<MailExtendedSearchFieldName, string>;

export type MailExtendedSearchFieldLabels = Record<MailExtendedSearchFieldName, string>;

export const MAIL_EXTENDED_SEARCH_BACKEND_KEYS: Record<MailExtendedSearchFieldName, string> = {
	contact: 'DesktopModuleMailsApplicationExtendedSearchPropertyContact',
	fileType: 'DesktopModuleMailsApplicationExtendedSearchPropertyFileType',
	filename: 'DesktopModuleMailsApplicationExtendedSearchPropertyFilename',
	subject: 'DesktopModuleMailsApplicationExtendedSearchPropertySubject',
	text: 'DesktopModuleMailsApplicationExtendedSearchPropertyText',
};

export const createEmptyMailExtendedSearchValues = (): MailExtendedSearchValues => ({
	contact: '',
	fileType: '',
	filename: '',
	subject: '',
	text: '',
});

export const normalizeMailExtendedSearchValues = (values: MailExtendedSearchValues): MailExtendedSearchValues => ({
	contact: values.contact.trim(),
	fileType: values.fileType.trim(),
	filename: values.filename.trim(),
	subject: values.subject.trim(),
	text: values.text.trim(),
});

export const mailExtendedSearchFieldHasAcceptedLength = (
	fieldName: MailExtendedSearchFieldName,
	value: string
): boolean => {
	const normalizedValue = value.trim();
	if (normalizedValue === '') {
		return false;
	}

	if (fieldName === 'fileType') {
		return true;
	}

	return normalizedValue.length >= MAIL_EXTENDED_SEARCH_MINIMUM_QUERY_LENGTH;
};

export const hasMailExtendedSearchMinimumLength = (values: MailExtendedSearchValues): boolean =>
	MAIL_EXTENDED_SEARCH_FIELD_NAMES.some((fieldName) =>
		mailExtendedSearchFieldHasAcceptedLength(fieldName, values[fieldName])
	);

export const countMailExtendedSearchFilledFields = (values: MailExtendedSearchValues): number =>
	MAIL_EXTENDED_SEARCH_FIELD_NAMES.filter((fieldName) => values[fieldName].trim() !== '').length;

export const serializeMailExtendedSearchHiddenTerm = (values: MailExtendedSearchValues): string => {
	const normalizedValues = normalizeMailExtendedSearchValues(values);
	if (countMailExtendedSearchFilledFields(normalizedValues) === 0) {
		return '';
	}

	const backendPayload = MAIL_EXTENDED_SEARCH_FIELD_NAMES.reduce<Record<string, string>>((payload, fieldName) => {
		payload[MAIL_EXTENDED_SEARCH_BACKEND_KEYS[fieldName]] = normalizedValues[fieldName];

		return payload;
	}, {});

	return JSON.stringify(backendPayload);
};

export const parseMailExtendedSearchHiddenTerm = (hiddenTerm: string): MailExtendedSearchValues => {
	if (hiddenTerm.trim() === '') {
		return createEmptyMailExtendedSearchValues();
	}

	try {
		const parsedPayload = JSON.parse(hiddenTerm) as Record<string, unknown>;
		return MAIL_EXTENDED_SEARCH_FIELD_NAMES.reduce<MailExtendedSearchValues>((values, fieldName) => {
			const value = parsedPayload[MAIL_EXTENDED_SEARCH_BACKEND_KEYS[fieldName]];
			values[fieldName] = typeof value === 'string' ? value : '';

			return values;
		}, createEmptyMailExtendedSearchValues());
	} catch {
		return createEmptyMailExtendedSearchValues();
	}
};

export const createMailExtendedSearchVisibleTerm = (
	values: MailExtendedSearchValues,
	labels: MailExtendedSearchFieldLabels
): string =>
	MAIL_EXTENDED_SEARCH_FIELD_NAMES.map((fieldName) => ({
		label: labels[fieldName],
		value: values[fieldName].trim(),
	}))
		.filter((entry) => entry.value !== '')
		.map((entry) => `${entry.label}:(${entry.value}) `)
		.join('');
